import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, finalize, Observable, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';
import { Guid } from '@web/utils';
import { AdminOrderShippingAddressFormComponent } from '../admin-order-shipping-address-form/admin-order-shipping-address-form.component';
import { AdminOrderShippingAddressForm } from '../../models/admin-order-shipping-address-form';
import { AdminOrderDataSourceService } from '../../services/admin-order-data-source.service';

@Component({
  selector: 'app-admin-order-shipping-address-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertsComponent,
    SpinnerFilledComponent,
    AdminOrderShippingAddressFormComponent,
  ],
  templateUrl: './admin-order-shipping-address-edit.component.html',
  styleUrl: './admin-order-shipping-address-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminOrderShippingAddressEditComponent {
  public readonly orderId = input.required<Guid>();
  public readonly shipmentId = input.required<Guid>();
  public readonly submitEvent = output<void>();

  protected readonly pending = signal(false);
  protected readonly orderId$ = this.orderIdSource();
  protected readonly shipmentId$ = this.shipmentIdSource();
  protected readonly data$ = this.dataSource();

  /* prettier-ignore */
  private readonly orderDataSource = inject(AdminOrderDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public handleSubmitClickEvent(data: AdminOrderShippingAddressForm): void {
    this.markAsPending();

    this.orderDataSource
      .updateShippingAddressByFormData({
        orderId: this.orderId(),
        shipmentId: this.shipmentId(),
        data,
      })
      .pipe(finalize(() => this.markAsResolved()))
      .subscribe({
        next: () => this.submitEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private markAsPending(): void {
    this.pending.set(true);
  }

  private markAsResolved(): void {
    this.pending.set(false);
  }

  private orderIdSource(): Observable<Guid> {
    return toObservable(this.orderId);
  }

  private shipmentIdSource(): Observable<Guid> {
    return toObservable(this.shipmentId);
  }

  private dataSource(): Observable<AdminOrderShippingAddressForm> {
    return combineLatest({
      orderId: this.orderId$,
      shipmentId: this.shipmentId$,
    }).pipe(
      switchMap(({ orderId, shipmentId }) =>
        this.orderDataSource.fetchShippingAddressFormById({
          orderId,
          shipmentId,
        }),
      ),
    );
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
